/**
 * Individual onboarding step card component
 * Displays an active/incomplete step with icon, title, description, and action button
 */

import React from "react";
import { Badge, Box, Paper, Stack, Text } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import type { OnboardingStepItem } from "../types";
import {
  getStepConfig,
  getStepDescription,
  getStepTitle,
} from "../config/onboardingStepsConfig";
import { getLevelConfig, type LevelType } from "../utils/levelUtils";

interface OnboardingStepCardProps {
  step: OnboardingStepItem;
  onStepClick: (stepId: string) => void;
  loading?: boolean;
  level: LevelType;
}

/**
 * Card component for displaying an active onboarding step
 * Only used for incomplete steps (completed steps shown in CompletedStepsSection)
 */
const OnboardingStepCard: React.FC<OnboardingStepCardProps> = ({
  step,
  onStepClick,
  level,
}) => {
  const config = getStepConfig(step.id);
  const levelConfig = getLevelConfig(level);

  // If config doesn't exist, don't render the card
  if (!config) {
    console.warn(`Skipping card for unknown step: ${step.id}`);
    return null;
  }

  const IconComponent = config.icon;

  // Get dynamic title and description based on metadata
  const title = getStepTitle(step.id, step.metadata);
  const description = getStepDescription(step.id, step.metadata);

  // Extract RGB values from level color for hover shadow
  const getLevelColorRGBA = (color: string, alpha: number) => {
    // Convert hex to RGB
    const hex = color.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <Paper
      shadow="lg"
      p="md"
      radius="md"
      style={{
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#ffffff",
        border: `2px solid ${levelConfig.color}`,
        transition: "all 0.3s ease",
        cursor: "pointer",
        maxWidth: "200px",
        minWidth: "180px",
        width: "100%",
        aspectRatio: "1",
        display: "flex",
        flexDirection: "column",
      }}
      onClick={() => onStepClick(step.id)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-6px) scale(1.02)";
        e.currentTarget.style.boxShadow = `0 12px 24px ${getLevelColorRGBA(levelConfig.color, 0.25)}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.15)";
      }}
    >
      {/* Status Badge */}
      <Box
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 1,
        }}
      >
        <Badge
          size="md"
          color={levelConfig.badgeColor}
          variant="filled"
          rightSection={<IconChevronRight size={14} />}
          style={{
            fontWeight: 600,
            boxShadow: `0 2px 8px ${getLevelColorRGBA(levelConfig.color, 0.3)}`,
          }}
        >
          Start
        </Badge>
      </Box>

      <Stack gap="sm" style={{ flex: 1, position: "relative", zIndex: 1 }}>
        {/* Icon */}
        <Box
          style={{
            width: 48,
            height: 48,
            borderRadius: "10px",
            background: config.gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "transform 0.3s ease",
          }}
        >
          <IconComponent size={24} color="white" stroke={1.5} />
        </Box>

        {/* Title */}
        <Text size="md" fw={700} c="dark" lineClamp={2}>
          {title}
        </Text>

        {/* Description */}
        <Text
          size="xs"
          c="dimmed"
          lineClamp={3}
          style={{ lineHeight: 1.5, opacity: 0.9 }}
        >
          {description}
        </Text>
      </Stack>
    </Paper>
  );
};

export default OnboardingStepCard;
